<template lang="pug">

    div

        //- Auftraggeber
        dropdown-field(
            v-if='$auth.isZmuekish'
            label='Auftraggeber'
            optionLabel='name'
            optionValue='id'
            v-bind:options='purchaserModels'
            v-bind:isRequired='true'
            v-model='companyId'
        )
            error(:$v='$v.companyId' validator='required')
                | Darf nicht leer sein. Bitte einen Auftraggeber auswählen

        //- Kontaktperson
        person-dropdown-field(
            label='Kontaktperson'
            v-bind:company-model='companyModel'
            v-bind:person-models='personModels'
            v-model='contactPersonId'
            v-show="companyId"
        )
        
        //- Kampagne
        campaign-dropdown-field(
            :campaign-models='campaignModels'
            :company-model='companyModel'
            v-model='campaignId'
            v-if="companyId"
        )

        //- TODO Kostenstelle
        text-field(label='Kostenstelle' v-model="costCenter")

        //- Korrespondenzadresse
        address-dropdown-field(
            label='Korrespondenzadresse'
            optionLabel='name'
            optionValue='id'
            v-bind:address-models='correspondenceAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:force-correspondence-address='true'
            v-bind:person-models='personModels'
            v-bind:required='true'
            v-model='correspondenceAddressId'
            v-if="companyId"
        )
            error(:$v='$v.correspondenceAddressId' validator='required')
                | Darf nicht leer sein. Bitte eine Korrespondenzadresse auswählen

        //- Rechnungsadresse
        address-dropdown-field(
            label='Rechnungsadresse'
            option-label='name'
            option-value='id'
            v-bind:address-models='billingAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:force-billing-address='true'
            v-bind:person-models='personModels'
            v-bind:required='true'
            v-model='billingAddressId'
            v-if="companyId"
        )
            error(:$v='$v.billingAddressId' validator='required')
                | Darf nicht leer sein. Bitte eine Rechnungsadresse auswählen

        //- Interne Notiz
        textarea-field(v-if='$auth.isZmuekish' label='Interne Notiz' v-model="note")

        //- Agentur
        dropdown-field(
            label='Agentur'
            optionLabel='name'
            optionValue='id'
            v-bind:options='agencyModels'
            v-model='agencyId'
            v-show="companyId"
        )

        //- Belegexemplare
        specimen-copy-form(
            ref="specimenCopyForm"
            label='Belegexemplare'
            v-bind:address-models='shippingAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:person-models='personModels'
            v-bind:recipient-person-models='personModels'
            v-model='specimenCopy'
            v-if="companyModel.id"
        )

        //- Belegexemplare (nur hausintern)
        specimen-copy-form(
            v-if='$auth.isZmuekish'
            ref="specimenCopyFormInternal"
            label='Belegexemplare (nur hausintern)'
            v-bind:address-models='internalShippingAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:person-models='personModels'
            v-bind:recipient-person-models='mediaProductionPersonModels'
            v-model='specimenCopyInternal'
            v-show="companyModel.id"
        )

        //- Verantwortlicher Medienproduktioner
        dropdown-field(
            v-if='$auth.isZmuekish'
            label='Medienproduktioner'
            optionLabel='name'
            optionValue='id'
            v-bind:isRequired='true'
            v-bind:options='mediaProductionUserOptions'
            v-model='mediaProductionId'
        )
            error(:$v='$v.mediaProductionId' validator='required')
                | Darf nicht leer sein. Bitte einen Medienproduktioner auswählen

</template>

<script>
    import * as R from 'ramda'
    import { required, requiredIf } from 'vuelidate/lib/validators'
    import * as AddressUtils from '@/utils/address'
    import { validate_composite } from '@/utils/validate'
    import AddressDropdownField from '@/components/addresses/DropdownField.vue'
    import CampaignDropdownField from '@/components/campaigns/DropdownField.vue'
    import DropdownField from '@/components/form/DropdownField.vue'
    import Error from '@/components/form/FieldError.vue'
    import NumberField from '@/components/form/NumberField.vue'
    import PersonDropdownField from '@/components/persons/DropdownField'
    import SpecimenCopyForm from './SpecimenCopyForm.vue'
    import TextareaField from '@/components/form/TextareaField.vue'
    import TextField from '@/components/form/TextField.vue'

    //noinspection ReservedWordAsName,JSUnusedGlobalSymbols
    export default {

        components: {
            AddressDropdownField, CampaignDropdownField, DropdownField, Error, NumberField, PersonDropdownField, TextareaField,
            TextField, SpecimenCopyForm
        },

        computed: {

            agencyModels() {
                return this.$store.getters['companies/allByIsAgencyOfCompanyId'](this.companyId) || []
            },

            agencyId: {
                get() { return this.value.agency },
                set(value) { this.updateValue('agency', value) }
            },

            billingAddressId: {
                get() { return this.value.billingAddress },
                set(value) { this.updateValue('billingAddress', value) }
            },

            billingAddressModels() {
                return R.compose(

                    // Name der Standardrechungsadresse formatieren
                    R.map(AddressUtils.format_name_by_default_billing_address),

                    // Nach Standardrechnungsadresse sortieren
                    AddressUtils.sort_by_is_default_shipping_address,

                    // Rechnungsadresse des Unternehmens holen
                    this.$store.getters['addresses/allByCompanyIdAndIsBillingAddress']

                )(this.companyId)
            },

            campaignModels() {
                return this.$store.getters['campaigns/allByCompanyId'](this.companyId) || []
            },

            campaignId: {
                get() { return this.value.campaign },
                set(value) { this.updateValue('campaign', value) }
            },

            correspondenceAddressId: {
                get() { return this.value.correspondenceAddress },
                set(value) { this.updateValue('correspondenceAddress', value) }
            },

            correspondenceAddressModels() {
                return R.compose(

                    // Name der Standardrechungsadresse formatieren
                    R.map(AddressUtils.format_name_by_default_correspondence_address),

                    // Nach Standardrechnungsadresse sortieren
                    AddressUtils.sort_by_is_default_correspondence_address,

                    // Rechnungsadresse des Unternehmens holen
                    this.$store.getters['addresses/allByCompanyIdAndIsCorrespondenceAddress']

                )(this.companyId)
            },

            companyId: {
                get() { return this.value.company },
                set(companyId) { this.updateValue('company', companyId) }
            },

            companyModel() {
                return this.$store.getters['companies/byId'](this.companyId) || {}
            },

            costCenter: {
                get() { return this.value.costCenter },
                set(value) { this.updateValue('costCenter', value) }
            },

            contactPersonId: {
                get() { return this.value.contact || 0 },
                set(value) { this.updateValue('contact', value) }
            },

            defaultBillingAddressModel() {
                return R.compose(

                    // Adresse laden
                    this.$store.getters['addresses/byId'],

                    // ID der Standard-Rechnungsadresse des Unternehmens lesen
                    R.prop('defaultBillingAddress')

                )(this.companyModel)
            },

            note: {
                get() { return this.value.note },
                set(value) { this.updateValue('note', value) }
            },

            mediaProductionId: {
                get() { return this.value.mediaProduction },
                set(value) { this.updateValue('mediaProduction', value) }
            },

            mediaProductionUserOptions() {
                return this.mediaProductionUserModels.map(user => {
                    const person = R.find(R.propEq('id', user.person), this.mediaProductionPersonModels)
                    return { id: user.id, name: person ? `${person.firstName} ${person.lastName}` : String(user.id) }
                })
            },

            personModels() {
                return this.$store.getters['persons/allByCompanyId'](this.companyId) || []
            },

            mediaProductionPersonId() {
                const userId = R.defaultTo(this.companyModel.mediaProductionUser, this.value.mediaProduction)
                if (R.isNil(userId)) {
                    return undefined
                }
                const user = R.find(R.propEq('id', userId), this.mediaProductionUserModels)
                return user ? user.person : undefined
            },

            shippingAddressModels() {
                return this.$store.getters['addresses/allByCompanyIdAndIsShippingAddress'](this.companyId) || []
            },

            specimenCopy: {
                get() { return this.value.specimenCopy || {} },
                set(value) { this.updateValue('specimenCopy', value) }
            },

            specimenCopyInternal: {
                get() { return this.value.specimenCopyInternal || {} },
                set(value) { this.updateValue('specimenCopyInternal', value) }
            },

        },

        created() {

            // Wenn Benutzerrolle "Kunde"
            if (this.$auth.activeRole === 'CUSTOMER') {
                // Value-Model klonen
                const valueModel = R.clone(this.value)

                // Benutzerunternehmen holen
                const companyModel = this.$store.getters['companies/byId'](this.$auth.companyId)

                // Benutzerunternehmen als Auftraggeber setzen
                valueModel.company = companyModel.id

                // Medienproduktioner des Unternehmens setzen
                valueModel.mediaProduction = companyModel.mediaProductionUser

                // Value-Model submitten
                this.$emit('input', valueModel)
            }

        },

        methods: {

            updateValue(prop, value) {
                this.$emit('input', R.assoc(prop, value, this.value))
            },

            validate() {
                return Promise.all([
                    validate_composite.bind(this).call().then(() => true).catch(() => false),
                    this.$refs.specimenCopyForm.validate(),
                    this.$refs.specimenCopyFormInternal
                        ? this.$refs.specimenCopyFormInternal.validate()
                        : Promise.resolve(true)
                ]).then(results => R.all(R.equals(true), results))

            },

        },

        props: {

            countryModels: {
                type: Array,
                required: true
            },

            internalShippingAddressModels: {
                type: Array,
                required: true
            },

            mediaProductionPersonModels: {
                type: Array,
                required: true
            },

            mediaProductionUserModels: {
                type: Array,
                required: true
            },

            purchaserModels: {
                type: Array,
                required: true
            },

            value: {
                type: Object,
                required: true
            }

        },

        validations: {

            billingAddressId: {
                required: requiredIf(vm => R.is(Number, vm.companyId))
            },

            correspondenceAddressId: {
                required: requiredIf(vm => R.is(Number, vm.companyId))
            },

            mediaProductionId: {
                required: requiredIf(vm => vm.$auth.isZmuekish && R.is(Number, vm.companyId))
            },

            companyId: { required },
        },

        watch: {

            async companyId(companyId) {
                // Wenn Unternehmen geleert wurden, nichts laden
                if (companyId === undefined) {
                    return undefined
                }

                const [ companyModel ] = await Promise.all([

                    // Unternehmen laden
                    this.$store.dispatch('companies/loadOne', { id: companyId }),

                    // Adressen des Unternehmens laden
                    this.$store.dispatch('addresses/loadAllByCompanyId', { companyId }),

                    // Kampagnen des Unterehmens laden
                    this.$store.dispatch('campaigns/loadAllByCompanyId', { companyId }),

                    // Agenturen laden
                    this.$store.dispatch('companies/loadAllByIsAgencyOfCompanyId', { companyId }),

                    // Personen laden
                    this.$store.dispatch('persons/loadAllByCompanyId', { companyId }),

                ])

                // Patch sofort ausloesen (vor dem optionalen States-Laden),
                // damit die Adressfelder vorausgefüllt werden.
                // Vorher stand $emit nach dem await loadOne – wenn defaultBillingAddress
                // null ist, warf billingAdressModel.country einen TypeError und
                // der Patch wurde nie emittiert.
                const mediaProduction = this.mediaProductionUserModels.some(
                    user => user.id === companyModel.mediaProductionUser
                )
                    ? companyModel.mediaProductionUser
                    : undefined

                this.$emit('input', {
                    ...this.value,
                    campaign: undefined,
                    billingAddress: companyModel.defaultBillingAddress || undefined,
                    correspondenceAddress: companyModel.defaultCorrespondenceAddress || undefined,
                    mediaProduction,
                })

                // Bundesländer der Standard-Rechnungsadresse laden (optional)
                if (companyModel.defaultBillingAddress) {
                    const billingAdressModel = await this.$store.dispatch('addresses/loadOne', { id: companyModel.defaultBillingAddress })
                    if (billingAdressModel?.country) {
                        await this.$store.dispatch('states/loadAllByCountry', { countryId: billingAdressModel.country })
                    }
                }
            },

            mediaProductionPersonId(personId) {
                // Hausinternes Belegexemplar-Empfänger vorbelegen (nur wenn noch nicht gesetzt)
                if (!R.isNil(personId) && R.isNil(this.specimenCopyInternal.recipient)) {
                    this.updateValue('specimenCopyInternal', {
                        ...this.specimenCopyInternal,
                        recipient: personId,
                    })
                }
            }

        }

    }

</script>
